import type { WritableDraft } from 'immer';

import {
  FieldValidationLevel,
  PricingMode,
  SecurityTypesUtils,
  SideEnum,
  durationStringToSeconds,
  format,
  isBasis,
  isCalendarSpread,
  isOption,
  toBig,
  toBigWithDefault,
  type DateField,
  type FieldValidationRule,
  type NumericField,
} from '@talos/kyoko';
import Big from 'big.js';
import { OrderStrategiesEnum } from '../../../tokens/orderStrategy';
import { getCareOrderQuantityValidationResult, isCareOrWorkingOrderParentType } from '../NewRFQ/tradeCareOrder';
import { validateQuantity } from './rules';
import type { OrderFormState, OrderState } from './types';

/** Strategies that use ClipSize, ClipInterval, and optional schedule window (Start/End). */
const CLIP_SCHEDULE_STRATEGIES = new Set<string>([
  OrderStrategiesEnum.Quantizer,
  OrderStrategiesEnum.MarketQuantizer,
  OrderStrategiesEnum.SteadyPace,
  OrderStrategiesEnum.SteadyPaceRefRate,
  OrderStrategiesEnum.RefRatePegSteadyPace,
  OrderStrategiesEnum.SteadyPaceStop,
]);

export function isClipScheduleStrategy(name: string | undefined): boolean {
  return name != null && CLIP_SCHEDULE_STRATEGIES.has(name);
}

export const quantityValidation: FieldValidationRule<NumericField, WritableDraft<OrderState>> = (field, context) => {
  if (!context || (!field.isRequired && !field.value)) {
    return null;
  }
  const { form, referenceData } = context;
  const security = form?.symbolField.value;
  if (!security) {
    return null;
  }

  const quantity = new Big(field.value || 0);
  const orderCurrency = form.orderCurrencyField.value || '';
  return validateQuantity(security, quantity, orderCurrency, referenceData.currencies.currenciesBySymbol);
};

export const careOrderQuantityValidation: FieldValidationRule<NumericField, WritableDraft<OrderState>> = (
  field,
  context
) => {
  const orderQty = field.value;
  // Only relevant if there is a care/working parent assigned.
  if (
    context?.form.parentIDField.value == null ||
    !isCareOrWorkingOrderParentType(context.form.parentType) ||
    !context.form.assignToParentField.value ||
    !orderQty
  ) {
    return null;
  }

  return getCareOrderQuantityValidationResult({
    orderQty,
    currentCareOrder: context.currentCareOrder,
    orderBeingModified: context.orderBeingModified,
  });
};

export const notInPastValidation: FieldValidationRule<DateField, WritableDraft<OrderState>> = (field, context) => {
  if (!context || !field.hasValue) {
    return null;
  }

  const now = new Date();
  const time = field.value!;

  if (now.valueOf() - new Date(time).valueOf() > 0) {
    return {
      message: `Time cannot be in the past`,
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

export const endTimeValidation: FieldValidationRule<DateField, WritableDraft<OrderState>> = (field, context) => {
  if (!context || !context.form.parameters.StartTime.hasValue || !context.form.parameters.EndTime.hasValue) {
    return null;
  }

  const startTime = context.form.parameters.StartTime.value;
  const endTime = context.form.parameters.EndTime.value;

  if (new Date(startTime).valueOf() - new Date(endTime).valueOf() > 0) {
    return {
      message: `End Time cannot be before Start Time`,
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

export const optionPriceRangeValidation: FieldValidationRule<NumericField, WritableDraft<OrderState>> = (
  field,
  context
) => {
  if (!context || !context.form.priceField.hasValue || !context.form.priceField.isRequired) {
    return null;
  }
  const { form } = context;
  const security = form.symbolField.value;
  const currency = form.orderCurrencyField.value;
  const priceMode = form.priceModeField.value;
  const side = form.sideField.value;
  const minOrderPrice = toBig(context.marketDataStatistics?.MinOrderPrice);
  const maxOrderPrice = toBig(context.marketDataStatistics?.MaxOrderPrice);

  if (!security || !isOption(security) || !minOrderPrice || !maxOrderPrice || priceMode !== PricingMode.Default) {
    return null;
  }
  const price = toBig(context.form.priceField.value);

  if (side === SideEnum.Buy && price?.gte(maxOrderPrice)) {
    return {
      message: `Price must be lower than ${format(maxOrderPrice, {
        spec: security.DefaultPriceIncrement,
      })} ${currency}`,
      level: FieldValidationLevel.Error,
    };
  } else if (side === SideEnum.Sell && price?.lte(minOrderPrice)) {
    return {
      message: `Price must be higher than ${format(minOrderPrice, {
        spec: security.DefaultPriceIncrement,
      })} ${currency}`,
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

// Confirmed with Zheng that the 0 rate is valid on Delta-1, Option Combos and Calendar Spreads (native)
// Spot, Perp, Futures, Options, Syntheitic Cross - rate must be greater than 0.
// Delta-1, Option Combos, Calendar Spreads (Basis) - rate can be negative, 0 or positive.
export const priceValidation: FieldValidationRule<NumericField, WritableDraft<OrderState>> = (field, context) => {
  if (!context || !context.form.priceField.hasValue || !context.form.priceField.isRequired) {
    return null;
  }
  const { form } = context;
  const security = form.symbolField.value;

  if (!security || isBasis(security) || isCalendarSpread(security)) {
    return null;
  }

  const price = toBig(context.form.priceField.value);

  if (price?.lte(0)) {
    return {
      message: `Price must be greater than 0`,
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

// Prediction market prices represent a probability and are always within 0-1.
// The lower bound (> 0) is already enforced by priceValidation, so this only caps the upper bound.
export const predictionPriceRangeValidation: FieldValidationRule<NumericField, WritableDraft<OrderState>> = (
  _field,
  context
) => {
  if (!context || !context.form.priceField.hasValue || !context.form.priceField.isRequired) {
    return null;
  }
  const security = context.form.symbolField.value;
  if (!SecurityTypesUtils.PredictionOutcome.isProductType(security)) {
    return null;
  }

  const price = toBig(context.form.priceField.value);
  if (price?.gt(1)) {
    return {
      message: `Price must be between 0 and 1`,
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

export const clipSizeValidation: FieldValidationRule<NumericField, WritableDraft<OrderState>> = (field, context) => {
  if (!context || !context.form.parameters.ClipSize.hasValue || !context.form.quantityField.hasValue) {
    return null;
  }

  const quantity = toBig(context.form.quantityField.value);
  if (quantity?.lt(context.form.parameters.ClipSize.value)) {
    return {
      message: `Clip Size is greater than quantity`,
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

export const clipIntervalValidation: FieldValidationRule<NumericField, WritableDraft<OrderState>> = (
  _field,
  context
) => {
  if (!context || !context.form.parameters.ClipInterval.hasValue) {
    return null;
  }

  // clip interval value is stored as `${number}${unit}`
  // so we need to extract the number part and ensure it is more than zero
  const clipIntervalValue = context.form.parameters.ClipInterval.value as string;
  const regex = /(\d+)/;
  const match = regex.exec(clipIntervalValue);
  const numberPart = match ? match[0] : '';

  if (toBigWithDefault(numberPart, 0)?.lte(0)) {
    return {
      message: `Clip Interval must be greater than zero`,
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

/**
 * Same max-clips model as OrderTrajectoryChart twapBounds calcClips.
 * If StartTime is unset, the window is taken from now (immediate start) through EndTime.
 */
function isClipScheduleUnderfilled(form: WritableDraft<OrderFormState>): boolean {
  const strategyName = form.strategyField.value?.Name;
  if (!isClipScheduleStrategy(strategyName)) {
    return false;
  }

  const { parameters, quantityField } = form;
  const clipSizeField = parameters.ClipSize as NumericField | undefined;
  const clipIntervalField = parameters.ClipInterval;
  const startTimeField = parameters.StartTime as DateField | undefined;
  const endTimeField = parameters.EndTime as DateField | undefined;

  if (!clipSizeField?.hasValue || !clipIntervalField?.hasValue || !endTimeField?.hasValue || !quantityField.hasValue) {
    return false;
  }

  const clipIntervalSec = durationStringToSeconds(clipIntervalField.value as string);
  if (clipIntervalSec == null || clipIntervalSec <= 0) {
    return false;
  }
  const clipIntervalMs = clipIntervalSec * 1000;

  const orderQty = toBig(quantityField.value);
  const clipSize = toBig(clipSizeField.value);
  if (!orderQty || !clipSize || clipSize.lte(0)) {
    return false;
  }

  const startMs = startTimeField?.hasValue ? new Date(startTimeField.value!).getTime() : Date.now();
  const endMs = new Date(endTimeField.value!).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return false;
  }

  const windowMs = endMs - startMs;
  const maxClips = Big(windowMs / clipIntervalMs).round(0);
  const schedulableQty = maxClips.times(clipSize);

  return orderQty.gt(schedulableQty);
}

/** Clip schedule window too short for quantity at current clip size/interval — shown on End Time only. */
export const clipScheduleUnderfillEndTimeValidation: FieldValidationRule<DateField, WritableDraft<OrderState>> = (
  _field,
  context
) => {
  if (!context || !isClipScheduleUnderfilled(context.form)) {
    return null;
  }
  return {
    message: 'The order duration is too short to fill the order.',
    level: FieldValidationLevel.Warning,
  };
};

export const greaterThanCumQtyValidation: FieldValidationRule<NumericField, WritableDraft<OrderState>> = (
  field,
  context
) => {
  if (!context || !context.orderBeingModified) {
    return null;
  }

  // Ensure that the quantity is not less than the modified order's CumQty
  if (field.value && toBigWithDefault(field.value, 0).gte(toBigWithDefault(context.orderBeingModified.CumQty, 0))) {
    return null;
  } else {
    return {
      message: `Quantity cannot be less than already executed quantity: ${context.orderBeingModified.CumQty}`,
      level: FieldValidationLevel.Error,
    };
  }
};
